import React, { useState, useEffect, useCallback } from 'react';
import './ExpensesInvoicedPanel.css';
import VenExpService from '../../../../AppSettings/services/VenExpService';
import OppExpService from '../../../../AppSettings/services/OppExpService';
import GenExpensesService from '../../../../AppSettings/services/GenExpensesService';
import FixBillService from '../../../../AppSettings/services/FixBillService';
import AgentsService from '../../../../AppSettings/services/AgentsService';
import InvoiceService from '../../../../AppSettings/services/InvoiceService';
import { normalizeToArray } from '../../../../helpers/responseHelpers';
import { buildInvoiceNumberMap, resolveInvoiceNumber } from '../../../../helpers/invoiceNumber';

interface InvoicedExpenseItem {
  source_type: string;
  id: string;
  description: string;
  amount: number;
  date: string | null;
  agent: string;
  invoiceid: string;
}

interface InvoiceGroup {
  invoiceid: string;
  displayNumber: string;
  items: InvoicedExpenseItem[];
  total: number;
}

interface AgentMap {
  [key: string | number]: {
    name: string;
    company?: string;
  };
}

interface ExpensesInvoicedPanelProps {
  onClose: () => void;
}

const SOURCE_LABELS: Record<string, string> = {
  vendor: 'Vendor',
  operating: 'Operating',
  general: 'General',
  fixed_bills: 'Fixed Bill',
};

const SOURCE_TAG_CLASS: Record<string, string> = {
  vendor: 'tag-vendor',
  operating: 'tag-operating',
  general: 'tag-general',
  fixed_bills: 'tag-fixedbill',
};

const fmt = (n: number) =>
  n.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const formatDate = (d: string | null) => {
  if (!d) return '';
  const date = new Date(d);
  if (Number.isNaN(date.getTime())) return d;
  return date.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
};

const resolveAgentName = (agentsMap: AgentMap, agentid: any): string => {
  if (!agentid || !agentsMap[agentid]) return 'Unknown Agent';
  const agent = agentsMap[agentid];
  return `${agent.name}${agent.company ? ` - ${agent.company}` : ''}`;
};

/**
 * Vendor/Operating expenses use `invoiced` field; General expenses use `invoiceid`.
 * This helper extracts whichever is set as the invoice identifier.
 */
const getInvoiceRef = (item: any): string | null => {
  if (item.invoiceid) return String(item.invoiceid);
  if (item.invoiced && item.invoiced !== true && item.invoiced !== 'true')
    return String(item.invoiced);
  if (item.invoiced === true || item.invoiced === 'true') return 'invoiced';
  return null;
};

const isInvoiced = (item: any): boolean => {
  return !!(item.invoiceid || item.invoiced);
};

const ExpensesInvoicedPanel: React.FC<ExpensesInvoicedPanelProps> = ({ onClose }) => {
  const [invoiceGroups, setInvoiceGroups] = useState<InvoiceGroup[]>([]);
  const [loading, setLoading] = useState(true);
  const [expandedInvoices, setExpandedInvoices] = useState<Set<string>>(new Set());

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const agentsData = await AgentsService.getAgents();
      const agentsMap: AgentMap = Array.isArray(agentsData)
        ? (agentsData as any[]).reduce((acc: AgentMap, agent: any) => {
            acc[agent.AgentID] = {
              name: `${agent.FirstName} ${agent.LastName}`,
              company: agent.Company,
            };
            return acc;
          }, {} as AgentMap)
        : {};

      const [vendorData, operatingData, generalDataRaw, fixedBillsData, invoicesData] = await Promise.all([
        VenExpService.getAllVenExpenses(),
        OppExpService.getAllOppExpenses(),
        GenExpensesService.getAllGenExpenses(),
        FixBillService.getAllFixBillExpenses(),
        InvoiceService.getAllInvoices().catch(() => []),
      ]);

      const generalData: any[] = normalizeToArray(generalDataRaw);
      const fixedBillsList = normalizeToArray(fixedBillsData);
      const invoiceNumberMap = buildInvoiceNumberMap(normalizeToArray(invoicesData));

      const allInvoicedItems: InvoicedExpenseItem[] = [];

      (vendorData || [])
        .filter(isInvoiced)
        .forEach((item: any) => {
          const ref = getInvoiceRef(item);
          if (!ref) return;
          allInvoicedItems.push({
            source_type: 'vendor',
            id: `ven-${item.ven_id}`,
            description: item.description || '',
            amount: Number.isFinite(parseFloat(item.amount)) ? parseFloat(item.amount) : 0,
            date: item.date_added ?? null,
            agent: resolveAgentName(agentsMap, item.agentid),
            invoiceid: ref,
          });
        });

      (operatingData || [])
        .filter(isInvoiced)
        .forEach((item: any) => {
          const ref = getInvoiceRef(item);
          if (!ref) return;
          allInvoicedItems.push({
            source_type: 'operating',
            id: `opp-${item.opp_id}`,
            description: item.description || '',
            amount: Number.isFinite(parseFloat(item.amount)) ? parseFloat(item.amount) : 0,
            date: item.date_added ?? null,
            agent: resolveAgentName(agentsMap, item.agentid),
            invoiceid: ref,
          });
        });

      (generalData || [])
        .filter(isInvoiced)
        .forEach((item: any) => {
          const ref = getInvoiceRef(item);
          if (!ref) return;
          allInvoicedItems.push({
            source_type: 'general',
            id: `gen-${item.ge_id ?? item.gen_id}`,
            description: item.description || '',
            amount: Number.isFinite(parseFloat(item.amount)) ? parseFloat(item.amount) : 0,
            date: item.date_added ?? null,
            agent: resolveAgentName(agentsMap, item.agentid),
            invoiceid: ref,
          });
        });

      fixedBillsList
        .filter(isInvoiced)
        .forEach((item: any) => {
          const ref = getInvoiceRef(item);
          if (!ref) return;
          allInvoicedItems.push({
            source_type: 'fixed_bills',
            id: `fb-${item.fbill_id ?? item.bill_id}`,
            description: item.description || '',
            amount: Number.isFinite(parseFloat(item.amount)) ? parseFloat(item.amount) : 0,
            date: item.date_added ?? null,
            agent: resolveAgentName(agentsMap, item.agentid),
            invoiceid: ref,
          });
        });

      const groupsMap: Record<string, InvoicedExpenseItem[]> = {};
      allInvoicedItems.forEach((item) => {
        if (!groupsMap[item.invoiceid]) {
          groupsMap[item.invoiceid] = [];
        }
        groupsMap[item.invoiceid].push(item);
      });

      const groups: InvoiceGroup[] = Object.entries(groupsMap)
        .map(([invoiceid, items]) => ({
          invoiceid,
          displayNumber: String(resolveInvoiceNumber(invoiceid, invoiceNumberMap) ?? invoiceid),
          items,
          total: items.reduce((sum, i) => sum + i.amount, 0),
        }))
        .sort((a, b) => b.invoiceid.localeCompare(a.invoiceid));

      setInvoiceGroups(groups);
      if (groups.length <= 5) {
        setExpandedInvoices(new Set(groups.map((g) => g.invoiceid)));
      }
    } catch (err) {
      console.error('Failed to fetch invoiced expense items:', err);
      setInvoiceGroups([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const toggleInvoice = (invoiceid: string) => {
    setExpandedInvoices((prev) => {
      const next = new Set(prev);
      if (next.has(invoiceid)) next.delete(invoiceid);
      else next.add(invoiceid);
      return next;
    });
  };

  const grandTotal = invoiceGroups.reduce((sum, g) => sum + g.total, 0);
  const totalItems = invoiceGroups.reduce((sum, g) => sum + g.items.length, 0);

  return (
    <>
      <div className="exp-invoiced-overlay" onClick={onClose} />
      <div className="exp-invoiced-panel">
        <div className="exp-invoiced-header">
          <h2>Already Invoiced</h2>
          <button className="exp-invoiced-close-btn" onClick={onClose}>
            &times;
          </button>
        </div>

        {!loading && invoiceGroups.length > 0 && (
          <div className="exp-invoiced-summary-bar">
            <span>
              {totalItems} item{totalItems !== 1 ? 's' : ''} across{' '}
              {invoiceGroups.length} invoice{invoiceGroups.length !== 1 ? 's' : ''}
            </span>
            <span className="exp-invoiced-summary-total">${fmt(grandTotal)}</span>
          </div>
        )}

        <div className="exp-invoiced-content">
          {loading ? (
            <div className="exp-invoiced-loading">
              <div className="exp-invoiced-spinner" />
              <span>Loading...</span>
            </div>
          ) : invoiceGroups.length === 0 ? (
            <div className="exp-invoiced-empty">
              <p>No invoiced expenses found.</p>
              <p>Expense items will appear here once invoiced.</p>
            </div>
          ) : (
            invoiceGroups.map((group) => {
              const isExpanded = expandedInvoices.has(group.invoiceid);
              return (
                <div key={group.invoiceid} className="exp-invoiced-invoice-group">
                  <div
                    className="exp-invoiced-invoice-header"
                    onClick={() => toggleInvoice(group.invoiceid)}
                  >
                    <div className="exp-invoiced-invoice-left">
                      <span className={`exp-invoiced-chevron ${isExpanded ? 'expanded' : ''}`}>
                        &#9654;
                      </span>
                      <span className="exp-invoiced-invoice-id">Invoice #{group.displayNumber}</span>
                      <span className="exp-invoiced-invoice-count">
                        {group.items.length} item{group.items.length !== 1 ? 's' : ''}
                      </span>
                    </div>
                    <span className="exp-invoiced-invoice-total">
                      ${fmt(group.total)}
                    </span>
                  </div>

                  {isExpanded && (
                    <div className="exp-invoiced-items-list">
                      {group.items.map((item) => (
                        <div key={item.id} className="exp-invoiced-item-row">
                          <div className="exp-invoiced-item-left">
                            <span className={`exp-invoiced-item-tag ${SOURCE_TAG_CLASS[item.source_type] || ''}`}>
                              {SOURCE_LABELS[item.source_type] || item.source_type}
                            </span>
                            <span className="exp-invoiced-item-desc">{item.description}</span>
                          </div>
                          <div className="exp-invoiced-item-right">
                            <span className="exp-invoiced-item-amount">${fmt(item.amount)}</span>
                            {item.date && (
                              <span className="exp-invoiced-item-date">{formatDate(item.date)}</span>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              );
            })
          )}
        </div>
      </div>
    </>
  );
};

export default ExpensesInvoicedPanel;
